import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { Task, TaskCreate } from "types/Task";
import { postTask } from "../methods/postTask";
import { TASKS_QUERY_KEY } from "./useTasksQuery";

/**
 * Stable key so consumers can observe an in-flight task POST via
 * `useIsMutating`. The focus-refetch guard (`useTaskWriteInFlight`) gates on
 * this so a GET cannot resolve before a newly-created row lands on the server and
 * momentarily drops it from cache.
 */
export const POST_TASK_MUTATION_KEY = ["cc-db-api", "postTask"];

/**
 * Creates a task row and appends it to the cache. Server idempotency on
 * (user_id, job_id) is the backstop; the cache deduplicates on `id` in onSuccess.
 */
export default function usePostTaskMutation() {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: POST_TASK_MUTATION_KEY,
    mutationFn: (task: TaskCreate) => postTask(task, getAccessToken),
    // Registration is fire-and-forget from the tool-result sweep and the on-load
    // reconciliation, and neither retries on its own: the sweep marks the result
    // processed, and reconciliation runs once per resolved config signature.
    // Retry transient failures (network / 5xx) so a flaky connection doesn't
    // leave a submitted job with no row (and so unpolled). A 4xx is terminal
    // (e.g. a 404 unowned thread) and is not retried.
    retry: (failureCount, error) => {
      const status = (error as { response?: Response })?.response?.status;
      if (status !== undefined && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    onError: (error) => {
      console.error("Task registration failed:", error);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) => {
        if (!prev) return [data];
        // Dedup: if the row already landed (idempotent server response), replace.
        const exists = prev.some((j) => j.id === data.id);
        return exists
          ? prev.map((j) => (j.id === data.id ? data : j))
          : [...prev, data];
      });
    },
  });
}
